import { useParams } from 'react-router';
import { useGetPlantOverviewQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-overview.ts';
import { Skeleton } from '@/components/ui/skeleton.tsx';
import { useState } from 'react';
import { getUptimeStatusConfig } from '@/components/uptime/uptime-status-config.ts';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover.tsx';
import { Button } from '@/components/ui/button.tsx';
import { cn } from '@/utils/utils.ts';
import { ActivityIcon } from 'lucide-react';
import { UptimeStatusTimeline } from '@/components/uptime/uptime-status-timeline.tsx';
import { useGetPlantUptimeStatusQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-uptime-status.ts';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';

const PlantUptimeStatusPopover = () => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const { plantId } = useParams();

  const { data: overview, isLoading: isPlantOverviewLoading } = useGetPlantOverviewQuery({
    plantId: plantId,
  });

  const { data: uptimeData, isLoading: isUptimeStatusPlantLoading } =
    useGetPlantUptimeStatusQuery({
      plantId: plantId,
      enabled: popoverOpen,
    });

  const currentStatus = overview?.mqttStatus ?? -1;
  const statusDisplayConfig = getUptimeStatusConfig(currentStatus);

  if (isPlantOverviewLoading) {
    return <Skeleton className="h-9 w-12" />;
  }

  return (
    <>
      <Popover
        open={popoverOpen}
        onOpenChange={(open: boolean) => {
          setPopoverOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            className={cn('relative h-9 w-12 border-2 p-0', statusDisplayConfig.borderColor)}
            variant="outline"
          >
            <div
              className={cn(
                'absolute -top-[5px] -right-[5px] size-2.5 rounded-full',
                statusDisplayConfig.backgroundColor
              )}
            ></div>
            <ActivityIcon className={cn(statusDisplayConfig.textColor)} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          onOpenAutoFocus={(event) => {
            event.preventDefault();
          }}
          align="end"
          className="w-full"
        >
          {isUptimeStatusPlantLoading ? (
            <div
              className={cn(
                'flex h-[256px] w-[249px] items-center justify-center',
                `min-[900px]:h-[128px] min-[900px]:w-[496px]`
              )}
            >
              <LoadingSpinner className="size-14" />
            </div>
          ) : (
            <UptimeStatusTimeline data={uptimeData} currentStatus={currentStatus} />
          )}
        </PopoverContent>
      </Popover>
    </>
  );
};

export default PlantUptimeStatusPopover;
